// =============================================================================
// DM call actions — the client side of the ring/accept/decline flow.
// =============================================================================
// These are imperative helpers (not hooks) so they can be invoked from button
// handlers, the CallManager's WS listener, and the service-worker message
// bridge alike. They drive the Zustand store, send WS signaling, set/clear the
// active voice connection (which mounts the LiveKit driver), and start/stop the
// ringtones. The CallManager component owns the inbound-event reactions.
// =============================================================================

import { useApp, type IncomingCall } from './store';
import { wsClient } from './ws';
import { primeAudio, startRingtone, stopRingtone } from './sounds';

/** Caller: ring a DM. Joins the room immediately (warm media path) and rings. */
export function startCall(dmChannelId: string, otherName: string): void {
  primeAudio();
  const st = useApp.getState();
  // Already in/awaiting this call — ignore double clicks.
  if (st.outgoingCall?.dmChannelId === dmChannelId) return;
  if (st.activeVoice?.channelId === dmChannelId) return;
  st.setActiveVoice({ serverId: '', channelId: dmChannelId, channelName: otherName, isDm: true });
  st.setOutgoingCall({ dmChannelId, toName: otherName });
  wsClient.send({ type: 'call.ring', channelId: dmChannelId });
  startRingtone('outgoing');
}

/** Caller: give up before the callee answers. */
export function cancelOutgoingCall(dmChannelId: string): void {
  stopRingtone();
  const st = useApp.getState();
  st.setOutgoingCall(null);
  wsClient.send({ type: 'call.cancel', channelId: dmChannelId });
  if (st.activeVoice?.channelId === dmChannelId) st.setActiveVoice(null);
}

/** Callee: accept an incoming call → join the room. */
export function acceptIncomingCall(call: IncomingCall): void {
  primeAudio();
  stopRingtone();
  const st = useApp.getState();
  st.setIncomingCall(null);
  wsClient.send({ type: 'call.accept', channelId: call.dmChannelId });
  const name = call.from?.displayName || call.from?.username || 'Call';
  st.setActiveVoice({ serverId: '', channelId: call.dmChannelId, channelName: name, isDm: true });
}

/** Callee: decline an incoming call. */
export function declineIncomingCall(call: IncomingCall): void {
  stopRingtone();
  const st = useApp.getState();
  st.setIncomingCall(null);
  wsClient.send({ type: 'call.decline', channelId: call.dmChannelId });
}

/** Hang up an in-progress call (leave the room + tell the peer). */
export function hangUpCall(dmChannelId: string): void {
  stopRingtone();
  const st = useApp.getState();
  st.setOutgoingCall(null);
  wsClient.send({ type: 'call.hangup', channelId: dmChannelId });
  if (st.activeVoice?.channelId === dmChannelId) st.setActiveVoice(null);
}
